import { useEffect, useState } from 'react';
import DashboardLayout from '@/components/dashboard/DashboardLayout';
import api from '@/lib/apiClient';
import { formatApiError } from '@/lib/utils';
import { showConfirm } from '@/components/ui/GlobalConfirm';
import {
  FolderOpen, Plus, Search, X, Loader2, Edit3, Trash2,
  ChevronDown, ChevronUp, Scale, Briefcase
} from 'lucide-react';

const IC = "w-full bg-white/[0.04] border border-[rgba(201,164,76,0.15)] text-[#F8F6F0] placeholder-[#888]/50 rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:border-[#C9A44C] focus:ring-1 focus:ring-[#C9A44C]/40 transition-all";

function ModalCarpeta({ carpeta, clientes, onClose, onSaved }) {
  const editando = !!carpeta;
  const [f, setF] = useState({
    nombre_causa: carpeta?.nombre_causa || '',
    materia: carpeta?.materia || '',
    cliente_id: carpeta?.cliente_id || '',
  });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault(); setErr(''); setSaving(true);
    try {
      if (editando) {
        await api.put(`/api/carpetas/${carpeta.id}`, f);
      } else {
        await api.post('/api/carpetas/', f);
      }
      onSaved();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail, 'Error al guardar'));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-md bg-[#0E0E0E] border border-[rgba(201,164,76,0.2)] rounded-2xl p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg" style={{ fontFamily: 'Playfair Display, serif' }}>
            {editando ? 'Editar carpeta' : 'Nueva carpeta'}
          </h3>
          <button onClick={onClose} className="text-[#888] hover:text-[#F8F6F0]"><X className="w-5 h-5" /></button>
        </div>
        {err && <div className="bg-red-500/10 border border-red-500/25 rounded-lg p-2.5 mb-4 text-red-400 text-xs">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <input className={IC} placeholder="Nombre de la causa *" value={f.nombre_causa} onChange={set('nombre_causa')} required />
          <input className={IC} placeholder="Materia (ej: civil, laboral, penal)" value={f.materia} onChange={set('materia')} />
          <select className={IC} value={f.cliente_id} onChange={set('cliente_id')}>
            <option value="" className="bg-[#141414]">Cliente (opcional)</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id} className="bg-[#141414]">
                {c.apellido ? `${c.apellido.toUpperCase()}, ${c.nombre}` : c.nombre}
              </option>
            ))}
          </select>
          <button type="submit" disabled={saving}
            className="w-full text-[#070707] font-semibold py-2.5 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editando ? 'Guardar cambios' : 'Crear carpeta')}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Carpetas() {
  const [carpetas, setCarpetas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [expandido, setExpandido] = useState(null);
  const [detalles, setDetalles] = useState({});

  const cargar = async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([
        api.get('/api/carpetas/'),
        api.get('/api/clientes/'),
      ]);
      setCarpetas(c.data);
      setClientes(cl.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const expandir = async (id) => {
    if (expandido === id) { setExpandido(null); return; }
    setExpandido(id);
    if (!detalles[id]) {
      try {
        const r = await api.get(`/api/carpetas/${id}`);
        setDetalles((p) => ({ ...p, [id]: r.data }));
      } catch {}
    }
  };

  const eliminar = async (id, nombre) => {
    if (!(await showConfirm(`¿Eliminar la carpeta "${nombre}"? Esta acción no se puede deshacer.`, { titulo: 'Eliminar carpeta' }))) return;
    await api.delete(`/api/carpetas/${id}`);
    cargar();
  };

  const filtradas = carpetas.filter((c) => {
    const t = `${c.nombre_causa || ''} ${c.materia || ''} ${c.cliente_nombre || ''}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  const MATERIAS_COLOR = {
    civil: 'text-blue-300 bg-blue-500/10 border-blue-500/20',
    laboral: 'text-green-300 bg-green-500/10 border-green-500/20',
    penal: 'text-red-300 bg-red-500/10 border-red-500/20',
    familia: 'text-purple-300 bg-purple-500/10 border-purple-500/20',
    comercial: 'text-orange-300 bg-orange-500/10 border-orange-500/20',
  };

  return (
    <DashboardLayout title="Carpetas de causas">
      <div className="mb-4 p-3 bg-[#C9A44C]/5 border border-[rgba(201,164,76,0.15)] rounded-xl text-xs text-[#aaa] leading-relaxed">
        Las <span className="text-[#C9A44C]">carpetas</span> agrupan varios expedientes bajo una misma causa o asunto.
        Útil cuando un cliente tiene múltiples expedientes relacionados (ej: un divorcio con causas en fuero civil, laboral y familia al mismo tiempo).
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#888]" />
          <input className={IC + ' pl-10'} placeholder="Buscar por causa, materia o cliente..."
            value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button onClick={() => setShowModal(true)}
          className="text-[#070707] font-semibold px-5 py-2.5 rounded-lg flex items-center gap-2 whitespace-nowrap"
          style={{ background: 'linear-gradient(135deg, #C9A44C, #E0C878)' }}>
          <Plus className="w-4 h-4" /> Nueva carpeta
        </button>
      </div>

      <p className="text-xs text-[#666] mb-3">{filtradas.length} carpeta{filtradas.length !== 1 ? 's' : ''}</p>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#C9A44C]" /></div>
      ) : filtradas.length === 0 ? (
        <div className="text-center py-20 text-[#666]">
          <FolderOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">{carpetas.length === 0 ? 'Sin carpetas creadas' : 'Sin resultados'}</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtradas.map((c) => {
            const det = detalles[c.id];
            const matColor = MATERIAS_COLOR[c.materia?.toLowerCase()] || 'text-[#888] bg-white/5 border-white/10';
            return (
              <div key={c.id} className={`bg-[#0E0E0E] border border-[rgba(201,164,76,0.12)] rounded-xl overflow-hidden transition-all ${expandido === c.id ? 'border-[#C9A44C]/25' : ''}`}>
                {/* Fila principal */}
                <div className="flex items-center justify-between gap-4 p-4 cursor-pointer hover:bg-white/[0.02] transition-all"
                  onClick={() => expandir(c.id)}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-[#C9A44C]/10 flex items-center justify-center flex-shrink-0">
                      <FolderOpen className="w-5 h-5 text-[#C9A44C]" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{c.nombre_causa}</div>
                      <div className="text-[11px] text-[#888] flex items-center gap-2 flex-wrap">
                        {c.cliente_nombre && <span>{c.cliente_nombre}</span>}
                        {c.total_expedientes > 0 && (
                          <span className="flex items-center gap-1">
                            <Scale className="w-3 h-3" />{c.total_expedientes} exp.
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {c.materia && (
                      <span className={`text-[9px] px-2 py-0.5 rounded-full border hidden sm:inline ${matColor}`}>
                        {c.materia}
                      </span>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setEditando(c); }}
                      className="p-1.5 text-[#888] hover:text-[#C9A44C] transition-colors">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); eliminar(c.id, c.nombre_causa); }}
                      className="p-1.5 text-[#888] hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {expandido === c.id ? <ChevronUp className="w-4 h-4 text-[#888]" /> : <ChevronDown className="w-4 h-4 text-[#888]" />}
                  </div>
                </div>

                {/* Expedientes de la carpeta */}
                {expandido === c.id && (
                  <div className="border-t border-white/5 px-4 pb-4 pt-3">
                    {!det ? (
                      <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-[#C9A44C]" /></div>
                    ) : !det.expedientes || det.expedientes.length === 0 ? (
                      <p className="text-xs text-[#555] py-3 text-center">Sin expedientes en esta carpeta</p>
                    ) : (
                      <div className="space-y-1.5">
                        {det.expedientes.map((e) => (
                          <div key={e.id} className="flex items-center gap-3 p-2.5 bg-white/[0.02] border border-white/5 rounded-lg">
                            <Briefcase className="w-3.5 h-3.5 text-[#888] flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium truncate">{e.caratula}</div>
                              <div className="text-[10px] text-[#888]">
                                {e.fuero && `${e.fuero} · `}{e.estado_procesal?.replace('_', ' ')}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <ModalCarpeta clientes={clientes} onClose={() => setShowModal(false)} onSaved={() => { setShowModal(false); cargar(); }} />
      )}
      {editando && (
        <ModalCarpeta carpeta={editando} clientes={clientes} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />
      )}
    </DashboardLayout>
  );
}
